/**
 * Jotai atoms for dictation state
 *
 * These atoms allow dictation state to be shared across all components,
 * solving the issue where multiple useDictationManager() calls would
 * each create separate state instances.
 */
import { atom } from "jotai";
import type { DictationSurface } from "~/services/webrtcTranscription";

/** Current audio level (0-1 normalized RMS amplitude) */
export const dictationAudioLevelAtom = atom<number>(0);

/** Whether speech is currently detected vs silence/noise */
export const dictationSpeechDetectedAtom = atom<boolean>(false);

/** Dictation status shared across all components */
export type DictationStatus = "inactive" | "connecting" | "active" | "stopping";
export const dictationStatusAtom = atom<DictationStatus>("inactive");

/** Which section initiated the current dictation session (for UI highlighting) */
export const dictationActiveSectionAtom = atom<string | null>(null);

/**
 * Which surface should own the *preconnected* dictation session.
 *
 * The WebRTC service is a singleton holding one session, and a session
 * negotiated for one surface cannot serve the other (different model,
 * different turn handling — see `startRecording`). Preconnecting for the wrong
 * surface is worse than not preconnecting at all: the first click tears the
 * warm connection down and renegotiates from scratch.
 *
 * Indy surfaces set this while they are open so `usePreconnectDictation`
 * warms the right session; clearing it falls back to Assessment.
 */
export const dictationSurfaceIntentAtom = atom<DictationSurface | null>(null);
